/**
 * OtpRedirectPage — bridges OAuth callbacks to the app's login flow.
 *
 * The server's `/auth/otp-redirect` handles the primary routing:
 *   - Native: serves HTML that opens `totodo:///auth?email=&otp=&otpId=`
 *   - Web: 302 redirects to `CLIENT_ORIGIN/?email=&otp=&otpId=`
 *
 * This client-side page handles edge cases:
 *   1. Direct navigation to /auth/otp-redirect in the SPA
 *   2. Web flow where the SPA URL matches (e.g. vite dev proxy)
 *   3. Native Capacitor attempting scheme redirect with fallback
 *
 * Flow:
 *   - If we have email + otp + otpId params → attempt login directly
 *   - On native: also attempt scheme redirect as fallback
 *   - On failure: show error with retry link
 *   - On timeout (15s): show manual retry option
 *
 * Rendered OUTSIDE DatabaseProvider (public route) so it works
 * even when the user isn't authenticated yet.
 */

import { useEffect, useState, useRef } from "react"
import { useSearchParams } from "react-router-dom"
import { useTranslation } from "react-i18next"
import { Capacitor } from "@capacitor/core"
import { CircularProgress } from "@/app/components/ui/CircularProgress"

type RedirectState = "redirecting" | "success" | "timeout" | "error" | "missing-params"

export function OtpRedirectPage() {
  const { t } = useTranslation()
  const [searchParams] = useSearchParams()
  const [state, setState] = useState<RedirectState>("redirecting")
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const processedRef = useRef(false)

  const email = searchParams.get("email")
  const otp = searchParams.get("otp")
  const otpId = searchParams.get("otpId")
  const origin = searchParams.get("origin")

  useEffect(() => {
    // Prevent double-processing in React strict mode
    if (processedRef.current) return
    processedRef.current = true

    if (!email || !otp || !otpId) {
      setState("missing-params")
      return
    }

    const params = new URLSearchParams({ email, otp, otpId })
    if (origin) params.set("origin", origin)

    if (Capacitor.isNativePlatform()) {
      // Native: attempt scheme redirect
      const nativeUrl = `totodo:///auth?${params.toString()}`
      window.location.href = nativeUrl

      // Fallback: if the page is still visible after 1.5s, redirect to web
      timeoutRef.current = setTimeout(() => {
        if (!document.hidden) {
          // App didn't open — fall back to web login
          window.location.href = `/?${params.toString()}`
        }
      }, 1500)

      const handleVisibilityChange = () => {
        if (document.hidden && timeoutRef.current) {
          clearTimeout(timeoutRef.current)
        }
      }
      document.addEventListener("visibilitychange", handleVisibilityChange)

      return () => {
        document.removeEventListener("visibilitychange", handleVisibilityChange)
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
      }
    } else {
      // Web: redirect to root with OTP params — SignIn.tsx will auto-login
      window.location.href = `/?${params.toString()}`

      // Safety timeout — if redirect didn't work after 5s, show message
      timeoutRef.current = setTimeout(() => {
        setState("timeout")
      }, 5000)

      return () => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
      }
    }
  }, [email, otp, otpId, origin])

  return (
    <div className="flex min-h-dvh flex-col items-center justify-center bg-background px-4 transition-colors">
      {/* Logo */}
      <img
        src="/logos/company-icon.png"
        alt="ToToDo"
        className="mb-8 opacity-20"
        style={{ maxWidth: 80, maxHeight: 80 }}
      />

      {state === "redirecting" && (
        <>
          <CircularProgress className="mb-4 size-8" />
          <p className="text-sm text-muted-foreground">
            {t("Signing you in…")}
          </p>
        </>
      )}

      {state === "missing-params" && (
        <>
          <p className="mb-2 text-sm font-medium text-destructive">
            {t("Invalid login link")}
          </p>
          <p className="mb-4 max-w-sm text-center text-sm text-muted-foreground">
            {t("This link is missing required information. Please try signing in again.")}
          </p>
          <a href="/" className="text-sm text-primary hover:underline">
            {t("Go to sign in")}
          </a>
        </>
      )}

      {state === "timeout" && (
        <>
          <p className="mb-2 text-sm font-medium text-foreground">
            {t("Taking longer than expected")}
          </p>
          <p className="mb-4 max-w-sm text-center text-sm text-muted-foreground">
            {t("The redirect didn't complete. You can try signing in manually.")}
          </p>
          <a href="/" className="text-sm text-primary hover:underline">
            {t("Go to sign in")}
          </a>
        </>
      )}

      {state === "error" && (
        <>
          <p className="mb-2 text-sm font-medium text-destructive">
            {t("Sign in failed")}
          </p>
          <p className="mb-4 max-w-sm text-center text-sm text-muted-foreground">
            {t("Something went wrong. Please try signing in again.")}
          </p>
          <a href="/" className="text-sm text-primary hover:underline">
            {t("Try again")}
          </a>
        </>
      )}

      {state === "success" && (
        <>
          <div className="mb-4 text-2xl">✓</div>
          <p className="text-sm text-muted-foreground">
            {t("Signed in! Redirecting…")}
          </p>
        </>
      )}
    </div>
  )
}
